// ME design-system components (D64) — a small, reusable, presentational set in the
// approved BatchDialer-style language (clean neutral canvas, blue primary, teal
// accent, soft pill tints). No business logic — prop-driven only. Reused across
// Campaigns / Dialer / Reports later. Built on shadcn primitives + Tailwind tokens.
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { ChevronDown, SlidersHorizontal } from "lucide-react";

// ---- formatPhone: normalize (mirrors SQL to_e164, 0005) then display ---------
// Display formatter (D69). Normalizes arbitrary input the SAME way SQL to_e164 does
// so display matches storage, THEN renders US 11-digit E.164 as +1(XXX) XXX-XXXX:
//   • null / empty            -> "—"
//   • already has "+"         -> strip to "+" + digits (keep as-is)
//   • 10 digits               -> +1 + digits
//   • 11 digits leading 1     -> + + digits
//   • +1XXXXXXXXXX            -> +1(XXX) XXX-XXXX
//   • non-US (+44…) / unknown -> the cleaned string, never blank, never throws
export function formatPhone(e164: string | null | undefined): string {
  if (e164 == null) return "—";
  const raw = String(e164).trim();
  if (raw === "") return "—";

  // normalize to an E.164-ish candidate (mirror of to_e164)
  let cand: string | null;
  if (raw.startsWith("+")) {
    cand = raw.replace(/[^0-9+]/g, ""); // keep leading + and digits
  } else {
    const d = raw.replace(/\D/g, "");
    if (d.length === 10) cand = "+1" + d;
    else if (d.length === 11 && d[0] === "1") cand = "+" + d;
    else cand = null; // unknown shape — fall through to the cleaned pass-through
  }

  // US 11-digit E.164 -> pretty
  const m = cand?.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1(${m[1]}) ${m[2]}-${m[3]}`;

  // non-US or unparseable: return the cleaned string, never blank
  const cleaned = raw.startsWith("+") ? raw.replace(/[^0-9+]/g, "") : raw.replace(/\D/g, "");
  return cleaned || raw;
}

// ---- Underline tab bar (prototype style; teal accent for List Stacking) ------
export function TabBar({ tabs, value, onChange, accent = "blue", className }: {
  tabs: { value: string; label: string }[]; value: string; onChange: (v: string) => void;
  accent?: "blue" | "teal"; className?: string;
}) {
  const activeCls = accent === "teal"
    ? "text-teal-600 border-teal-600 dark:text-teal-400 dark:border-teal-400"
    : "text-blue-600 border-blue-600 dark:text-blue-400 dark:border-blue-400";
  return (
    <div className={`flex gap-0.5 border-b border-border overflow-x-auto ${className ?? ""}`}>
      {tabs.map((t) => {
        const on = t.value === value;
        return (
          <button key={t.value} onClick={() => onChange(t.value)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
              on ? activeCls : "text-muted-foreground border-transparent hover:text-foreground"}`}>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// ---- Pills ------------------------------------------------------------------
const TINT: Record<string, string> = {
  green: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  amber: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  blue: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  teal: "bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-300",
  violet: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  grey: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400",
  red: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
};
export function Pill({ tone = "grey", dot, children }: { tone?: keyof typeof TINT; dot?: boolean; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${TINT[tone]}`}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current opacity-90" />}
      {children}
    </span>
  );
}
const STATUS_TONE: Record<string, keyof typeof TINT> = {
  new: "green", working: "blue", promoted: "teal", not_interested: "grey", ghosting: "amber", dead: "red",
};
export const StatusPill = ({ status }: { status: string }) => (
  <Pill tone={STATUS_TONE[status] ?? "grey"} dot>{status.replace("_", " ").replace(/^\w/, (c) => c.toUpperCase())}</Pill>
);
const CASE_TONE: Record<string, keyof typeof TINT> = { probate: "violet", foreclosure: "amber", divorce: "blue" };
export const CaseTypePill = ({ caseType }: { caseType: string | null }) =>
  caseType ? <Pill tone={CASE_TONE[caseType] ?? "grey"}>{caseType.replace(/^\w/, (c) => c.toUpperCase())}</Pill> : <span className="text-muted-foreground text-xs">—</span>;
export const LastCallPill = ({ value }: { value: string | null }) => {
  if (!value || value === "—") return <span className="text-muted-foreground text-xs">Never</span>;
  const tone: keyof typeof TINT = value === "connected" ? "green" : value === "no_answer" ? "amber" : value === "wrong" || value === "dead" ? "red" : "grey";
  return <Pill tone={tone}>{value.replace("_", " ")}</Pill>;
};
export const ScoreDot = ({ score }: { score: number | null }) => {
  const cls = score == null ? "bg-slate-300" : score >= 70 ? "bg-emerald-500" : score >= 40 ? "bg-amber-500" : "bg-red-500";
  return <span className={`inline-block h-2 w-2 rounded-full ${cls}`} />;
};

// ---- Stat tile + section card (reused across Campaigns/Dialer/Reports) -------
export function StatTile({ label, value, active, onClick }: { label: string; value: ReactNode; active?: boolean; onClick?: () => void }) {
  return (
    <div onClick={onClick}
      className={`rounded-lg border bg-card px-4 py-3 ${onClick ? "cursor-pointer hover:border-muted-foreground/30" : ""} ${active ? "border-blue-300 ring-1 ring-blue-200 dark:border-blue-800 dark:ring-blue-900" : "border-border"}`}>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground capitalize">{label}</div>
    </div>
  );
}
export function SectionCard({ title, sub, right, children, className }: { title: ReactNode; sub?: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <div className={`rounded-lg border border-border bg-card ${className ?? ""}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/70">
        <div><h3 className="text-sm font-semibold">{title}</h3>{sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}</div>
        {right}
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

// ---- RadioCards (single choice as selectable cards) -------------------------
export type RadioOption = { value: string; label: string; desc?: string; disabled?: boolean; note?: string };
export function RadioCards({ options, value, onChange, cols = 3 }: { options: RadioOption[]; value: string; onChange: (v: string) => void; cols?: number }) {
  return (
    <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" disabled={o.disabled} onClick={() => !o.disabled && onChange(o.value)}
            className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${o.disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${
              on ? "border-blue-500 bg-blue-50 dark:bg-blue-950/40" : "border-border bg-background hover:border-muted-foreground/40"}`}>
            <div className="flex items-center gap-2">
              <span className={`grid h-4 w-4 place-items-center rounded-full border ${on ? "border-blue-600 bg-blue-600" : "border-input"}`}>{on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}</span>
              <span className="text-sm font-semibold">{o.label}</span>
            </div>
            {o.desc && <div className="text-xs text-muted-foreground mt-1 ml-6">{o.desc}</div>}
            {o.note && <div className="text-[11px] text-amber-700 dark:text-amber-400 mt-1 ml-6">{o.note}</div>}
          </button>
        );
      })}
    </div>
  );
}

// ---- ChipMultiSelect (toggle chips for a multi-select) ----------------------
export function ChipMultiSelect({ options, selected, onToggle, empty }: {
  options: { value: string; label: string; sub?: string }[]; selected: string[]; onToggle: (v: string) => void; empty?: ReactNode;
}) {
  if (options.length === 0) return <p className="text-xs text-muted-foreground">{empty ?? "None available."}</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button key={o.value} type="button" onClick={() => onToggle(o.value)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${on ? "border-blue-500 bg-blue-600 text-white" : "border-border bg-background text-foreground hover:border-muted-foreground/40"}`}>
            {o.label}{o.sub && <span className={`ml-1 ${on ? "opacity-80" : "text-muted-foreground"}`}>{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ---- Toolbar + quick filters ------------------------------------------------
export const MeToolbar = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2 mb-3">{children}</div>
);
export type QuickChip = { key: string; label: string; count?: number; active?: boolean; dashed?: boolean };
export function QuickFilters({ chips, onToggle }: { chips: QuickChip[]; onToggle: (key: string) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      {chips.map((c) => (
        <button
          key={c.key}
          onClick={() => onToggle(c.key)}
          className={`h-8 rounded-full px-3 text-xs font-semibold inline-flex items-center gap-1.5 border transition-colors ${c.dashed ? "border-dashed" : ""} ${
            c.active ? "bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-950/40 dark:border-blue-900 dark:text-blue-300" : "bg-background border-border text-muted-foreground hover:border-muted-foreground/40"
          }`}
        >
          {c.label}{c.count != null && <b className="text-foreground">{c.count.toLocaleString()}</b>}
        </button>
      ))}
    </div>
  );
}

// ---- Select ▾ menu ----------------------------------------------------------
export function SelectMenu({
  headState, visibleCount, allCount, onSelectVisible, onSelectAll, onClear,
}: {
  headState: "none" | "some" | "all";
  visibleCount: number; allCount: number;
  onSelectVisible: () => void; onSelectAll: () => void; onClear: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 h-9">
          <span className={`grid h-4 w-4 place-items-center rounded border text-[10px] ${headState === "all" ? "bg-primary border-primary text-primary-foreground" : headState === "some" ? "bg-primary/30 border-primary" : "border-input"}`}>
            {headState === "all" ? "✓" : headState === "some" ? "–" : ""}
          </span>
          Select <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-52">
        <DropdownMenuItem onClick={onSelectVisible}>Select visible <span className="ml-auto text-muted-foreground">{visibleCount}</span></DropdownMenuItem>
        <DropdownMenuItem onClick={onSelectAll}>Select all <span className="ml-auto text-muted-foreground">{allCount.toLocaleString()}</span></DropdownMenuItem>
        <DropdownMenuItem onClick={onClear}>Clear selection</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---- Actions ▾ (grouped) ----------------------------------------------------
export type ActionItem = { label: string; icon?: ReactNode; danger?: boolean; disabled?: boolean; onSelect: () => void };
export type ActionGroup = { label: string; items: ActionItem[] };
export function ActionsMenu({ groups, disabled }: { groups: ActionGroup[]; disabled?: boolean }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" className="h-9 gap-1" disabled={disabled}>Actions <ChevronDown className="h-3.5 w-3.5 opacity-80" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {groups.map((g, gi) => (
          <div key={g.label}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">{g.label}</DropdownMenuLabel>
            {g.items.map((it) => (
              <DropdownMenuItem key={it.label} disabled={it.disabled} onSelect={it.onSelect} className={it.danger ? "text-red-600 focus:text-red-600" : ""}>
                {it.icon && <span className="w-4 text-center opacity-80">{it.icon}</span>}{it.label}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---- Filter drawer (right sheet) --------------------------------------------
export function FilterDrawer({
  open, onOpenChange, children, onApply, onReset,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; children: ReactNode;
  onApply: () => void; onReset: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[370px] sm:max-w-[370px] flex flex-col p-0">
        <SheetHeader className="px-5 py-4 border-b"><SheetTitle>Filter properties</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-auto px-5 py-4 space-y-4">{children}</div>
        <SheetFooter className="px-5 py-3 border-t flex-row gap-2">
          <Button variant="outline" className="flex-1" onClick={onReset}>Reset</Button>
          <Button className="flex-1" onClick={onApply}>Apply</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
export const FilterOpenButton = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} className="h-8 rounded-full px-3 text-xs font-semibold inline-flex items-center gap-1.5 border bg-background text-muted-foreground hover:border-muted-foreground/40">
    <SlidersHorizontal className="h-3.5 w-3.5" /> Filters
  </button>
);

// ---- Pager ------------------------------------------------------------------
function pageSeq(p: number, tp: number): (number | "…")[] {
  if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
  const set = [...new Set([1, 2, tp - 1, tp, p, p - 1, p + 1])].filter((x) => x >= 1 && x <= tp).sort((a, b) => a - b);
  const out: (number | "…")[] = []; let prev = 0;
  for (const x of set) { if (x - prev > 1) out.push("…"); out.push(x); prev = x; }
  return out;
}
export function Pager({
  page, pageSize, total, onPage, onPageSize,
}: { page: number; pageSize: number; total: number; onPage: (p: number) => void; onPageSize: (n: number) => void }) {
  const tp = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const Btn = ({ children, to: t, active, dis }: { children: ReactNode; to?: number; active?: boolean; dis?: boolean }) => (
    <button
      disabled={dis} onClick={() => t && onPage(t)}
      className={`min-w-8 h-8 px-2 rounded-md border text-sm font-semibold grid place-items-center ${active ? "bg-primary border-primary text-primary-foreground" : "bg-background border-border text-muted-foreground"} ${dis ? "opacity-40" : "hover:border-muted-foreground/40"}`}
    >{children}</button>
  );
  return (
    <div className="flex items-center gap-3 px-3.5 py-2.5 border-t bg-muted/30 text-sm">
      <div className="flex items-center gap-2 text-muted-foreground font-semibold">
        Rows
        <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} className="h-8 rounded-md border bg-background px-2 font-semibold">
          {[20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
      <div className="text-muted-foreground font-semibold">Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}</div>
      <div className="ml-auto flex items-center gap-1">
        <Btn to={1} dis={page === 1}>«</Btn>
        <Btn to={page - 1} dis={page === 1}>‹</Btn>
        {pageSeq(page, tp).map((s, i) => s === "…"
          ? <span key={`d${i}`} className="px-1 text-muted-foreground">…</span>
          : <Btn key={s} to={s} active={s === page}>{s}</Btn>)}
        <Btn to={page + 1} dis={page === tp}>›</Btn>
        <Btn to={tp} dis={page === tp}>»</Btn>
      </div>
    </div>
  );
}
